import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import { callAdmin } from '@/integrations/supabase/client';

interface SystemSetting {
  id: string;
  key: string;
  value: string | number | boolean | Record<string, unknown>;
  description: string;
  updated_at: string;
}

export const SystemSettings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSetting, setEditingSetting] = useState<SystemSetting | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const result = await callAdmin('get_system_settings')
      if (result.success) setSettings(result.data as SystemSetting[])
      else setError(result.error || 'Failed to fetch system settings')
    } catch (err) {
      setError('Failed to fetch system settings');
      console.error('Error fetching system settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSetting = (setting: SystemSetting) => {
    setEditingSetting(setting);
    setEditValue(typeof setting.value === 'string' ? setting.value : JSON.stringify(setting.value));
    setShowEditModal(true);
  };

  const handleUpdateSetting = async () => {
    if (!editingSetting) return;

    try {
      let parsedValue;
      try {
        parsedValue = JSON.parse(editValue);
      } catch {
        parsedValue = editValue;
      }

      const result = await callAdmin('update_system_setting', { key: editingSetting.key, value: parsedValue })
      if (result.success) { 
        setShowEditModal(false); 
        setEditingSetting(null);
        fetchSettings(); 
      }
      else setError(result.error || 'Failed to update setting')
    } catch (err) {
      setError('Failed to update setting');
      console.error('Error updating setting:', err);
    }
  };

  const getSettingType = (key: string) => {
    if (key.includes('price') || key.includes('rate') || key.includes('count')) return 'number';
    if (key.includes('mode') || key.includes('enabled') || key.includes('active')) return 'boolean';
    if (key.includes('url') || key.includes('email')) return 'url';
    return 'text';
  };

  const formatValue = (value: string | number | boolean | Record<string, unknown>) => {
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const getSettingIcon = (key: string) => {
    if (key.includes('site')) return '🏠';
    if (key.includes('conversation')) return '💬';
    if (key.includes('trial')) return '⏰';
    if (key.includes('commission')) return '💰';
    if (key.includes('maintenance')) return '🔧';
    if (key.includes('voice')) return '🎤';
    return '⚙️';
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">System Settings</h2>
        <p className="text-gray-600">Configure platform-wide settings and parameters</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {settings.map((setting) => (
          <Card key={setting.id} className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <span className="text-2xl mr-3">{getSettingIcon(setting.key)}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 capitalize">
                    {setting.key.replace(/_/g, ' ')}
                  </h3>
                  <p className="text-sm text-gray-600">{setting.description}</p>
                </div>
              </div>
              <button
                onClick={() => handleEditSetting(setting)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Edit
              </button>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-700">
                <span className="font-medium">Current Value:</span>
                <div className="mt-1 font-mono text-sm bg-muted text-foreground p-2 rounded border border-border">
                  {formatValue(setting.value)}
                </div>
              </div>
            </div>

            <div className="mt-4 text-xs text-gray-500">
              Last updated: {new Date(setting.updated_at).toLocaleString()}
            </div>
          </Card>
        ))}
      </div>

      {/* Edit Setting Modal */}
      {showEditModal && editingSetting && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit Setting: {editingSetting.key.replace(/_/g, ' ')}
              </h3>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">{editingSetting.description}</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Value
                </label>
                {getSettingType(editingSetting.key) === 'boolean' ? (
                  <select
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                ) : getSettingType(editingSetting.key) === 'number' ? (
                  <Input
                    type="number"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full"
                  />
                ) : (
                  <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    placeholder="Enter value"
                  />
                )}
              </div>

              <div className="mb-4">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Current Value:</span>
                  <div className="mt-1 font-mono text-xs bg-gray-100 p-2 rounded">
                    {formatValue(editingSetting.value)}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 hover:bg-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateSetting}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700"
                >
                  Update Setting
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Information */}
      <Card className="mt-8 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Settings Information</h3>
        <div className="space-y-3 text-sm text-gray-600">
          <div>
            <span className="font-medium">site_name:</span> The display name of your application
          </div>
          <div>
            <span className="font-medium">max_free_conversations:</span> Maximum conversations allowed for free users
          </div>
          <div>
            <span className="font-medium">trial_days:</span> Number of days for new user trials
          </div>
          <div>
            <span className="font-medium">affiliate_commission_rate:</span> Default commission rate for affiliate products (0.05 = 5%)
          </div>
          <div>
            <span className="font-medium">maintenance_mode:</span> Enable maintenance mode to restrict access
          </div>
          <div>
            <span className="font-medium">voice_feature_enabled:</span> Enable or disable voice feature site-wide
          </div>
          <div>
            <span className="font-medium">max_voice_tokens_per_month_free:</span> Maximum voice tokens per month for free users
          </div>
          <div>
            <span className="font-medium">max_voice_tokens_per_month_premium:</span> Maximum voice tokens per month for premium users
          </div>
        </div>
      </Card>
    </div>
  );
};
